import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from 'ui-components';
import { trackEvent } from '~/core/utils/trackEvent';
import { TTAContent } from '~/types/tta';

import { ContentItem } from './ContentItem';

interface CarouselProps {
  items: TTAContent[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export const Carousel: React.FC<CarouselProps> = ({
  items,
  selectedIndex,
  onSelect,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const [lastMoveTime, setLastMoveTime] = useState(0);
  const [lastMoveX, setLastMoveX] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  // Drag threshold in pixels - only start dragging after moving this distance
  const DRAG_THRESHOLD = 5;
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState(false);
  const [needsScrolling, setNeedsScrolling] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const animationRef = useRef<number | null>(null);

  // Initialize refs array and check if scrolling is needed
  useEffect(() => {
    itemRefs.current = items.map(() => null);
  }, [items]);

  // Check if scrolling is needed and detect mobile
  useEffect(() => {
    const checkScrolling = () => {
      if (carouselRef.current) {
        const { scrollWidth, clientWidth } = carouselRef.current;
        setNeedsScrolling(scrollWidth > clientWidth);
      }
    };

    const checkIsMobile = () => {
      // Check for touch capability and screen size
      const hasTouchScreen =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 1024; // Less than 'mac' breakpoint
      setIsMobile(hasTouchScreen && isSmallScreen);
    };

    checkScrolling();
    checkIsMobile();

    window.addEventListener('resize', () => {
      checkScrolling();
      checkIsMobile();
    });

    return () => {
      window.removeEventListener('resize', () => {
        checkScrolling();
        checkIsMobile();
      });
    };
  }, [items]);

  const scrollSelectedIntoView = (index: number) => {
    const selectedItem = itemRefs.current[index];
    if (!selectedItem || !carouselRef.current) return;

    const container = carouselRef.current;
    const containerRect = container.getBoundingClientRect();
    const itemRect = selectedItem.getBoundingClientRect();

    const isFullyVisible =
      itemRect.left >= containerRect.left &&
      itemRect.right <= containerRect.right;

    if (!isFullyVisible) {
      if (index === 0) {
        container.scrollTo({
          left: 0,
          behavior: 'smooth',
        });
      } else if (index === items.length - 1) {
        container.scrollTo({
          left: container.scrollWidth - container.offsetWidth,
          behavior: 'smooth',
        });
      } else {
        const isMovingRight = index > selectedIndex;
        const scrollLeft = isMovingRight
          ? selectedItem.offsetLeft
          : selectedItem.offsetLeft -
            container.offsetWidth +
            selectedItem.offsetWidth;

        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    }
  };

  // Scroll selected item into view when selection changes
  useEffect(() => {
    scrollSelectedIntoView(selectedIndex);
  }, [selectedIndex]);

  // Momentum scrolling with animation
  const applyMomentum = useCallback(() => {
    if (!carouselRef.current || Math.abs(velocity) < 0.1) {
      setVelocity(0);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const newScrollLeft = carouselRef.current.scrollLeft - velocity;
    const maxScroll =
      carouselRef.current.scrollWidth - carouselRef.current.clientWidth;

    // Infinite scroll behavior
    if (newScrollLeft < 0) {
      carouselRef.current.scrollLeft = maxScroll + newScrollLeft;
    } else if (newScrollLeft > maxScroll) {
      carouselRef.current.scrollLeft = newScrollLeft - maxScroll;
    } else {
      carouselRef.current.scrollLeft = newScrollLeft;
    }

    // Apply friction to velocity
    setVelocity((prev) => prev * 0.95);

    animationRef.current = requestAnimationFrame(applyMomentum);
  }, [velocity]);

  // Start momentum when dragging ends
  useEffect(() => {
    if (!isDragging && !isMouseDown && Math.abs(velocity) > 0.1) {
      animationRef.current = requestAnimationFrame(applyMomentum);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDragging, isMouseDown, velocity, applyMomentum]);

  // Enhanced onSelect with tracking
  const handleItemSelect = (index: number) => {
    // Track individual content item clicks
    trackEvent('foam-tta-carousel-item-click', {
      selectedIndex: index,
      totalItems: items.length,
      contentId: items[index]?.content_id || 'unknown',
      interactionType: hasDragged ? 'drag-select' : 'click-select',
    });

    onSelect(index);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    // Cancel any ongoing momentum animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Track drag start
    trackEvent('foam-tta-carousel-drag-start', {
      totalItems: items.length,
      selectedIndex,
      isMobile,
    });

    setIsMouseDown(true);
    setIsDragging(false); // Don't set dragging until we cross the threshold
    setHasDragged(false);
    setStartX(e.pageX - (carouselRef.current?.offsetLeft ?? 0));
    setScrollLeft(carouselRef.current?.scrollLeft ?? 0);
    setVelocity(0);
    setLastMoveTime(Date.now());
    setLastMoveX(e.pageX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown) return;

    const currentX = e.pageX;
    const x = currentX - (carouselRef.current?.offsetLeft ?? 0);
    const distance = Math.abs(x - startX);

    // Only start dragging if we've moved beyond the threshold
    if (!isDragging && distance > DRAG_THRESHOLD) {
      setIsDragging(true);
      setHasDragged(true);
    }

    if (!isDragging) return; // Don't scroll until we're actually dragging

    e.preventDefault();

    const currentTime = Date.now();
    const timeDiff = currentTime - lastMoveTime;

    // Calculate velocity for momentum
    if (timeDiff > 0) {
      const velocityCalc = ((currentX - lastMoveX) / timeDiff) * 16; // Convert to per-frame velocity
      setVelocity(velocityCalc);
    }

    setLastMoveTime(currentTime);
    setLastMoveX(currentX);

    const walk = (x - startX) * 1.5; // Slightly reduced sensitivity

    if (carouselRef.current) {
      const newScrollLeft = scrollLeft - walk;
      const maxScroll =
        carouselRef.current.scrollWidth - carouselRef.current.clientWidth;

      // Infinite scroll behavior during drag
      if (newScrollLeft < 0) {
        carouselRef.current.scrollLeft = maxScroll + newScrollLeft;
        setScrollLeft(maxScroll + newScrollLeft);
        setStartX(x);
      } else if (newScrollLeft > maxScroll) {
        carouselRef.current.scrollLeft = newScrollLeft - maxScroll;
        setScrollLeft(newScrollLeft - maxScroll);
        setStartX(x);
      } else {
        carouselRef.current.scrollLeft = newScrollLeft;
      }
    }
  };

  const handleMouseUp = () => {
    // Track drag end if user was dragging
    if (isDragging) {
      trackEvent('foam-tta-carousel-drag-end', {
        totalItems: items.length,
        selectedIndex,
        interactionType: 'mouse',
        hasDragged,
      });
    }

    setIsMouseDown(false);
    setIsDragging(false);
    // Reset hasDragged after a small delay to allow clicks to process
    setTimeout(() => setHasDragged(false), 10);
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    // Cancel any ongoing momentum animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Track touch drag start
    trackEvent('foam-tta-carousel-drag-start', {
      totalItems: items.length,
      selectedIndex,
      isMobile: true,
    });

    const touch = e.touches[0];
    setIsMouseDown(true);
    setIsDragging(false); // Don't set dragging until we cross the threshold
    setHasDragged(false);
    setStartX(touch.pageX - (carouselRef.current?.offsetLeft ?? 0));
    setScrollLeft(carouselRef.current?.scrollLeft ?? 0);
    setVelocity(0);
    setLastMoveTime(Date.now());
    setLastMoveX(touch.pageX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMouseDown) return;

    const touch = e.touches[0];
    const currentX = touch.pageX;
    const x = currentX - (carouselRef.current?.offsetLeft ?? 0);
    const distance = Math.abs(x - startX);

    // Only start dragging if we've moved beyond the threshold
    if (!isDragging && distance > DRAG_THRESHOLD) {
      setIsDragging(true);
      setHasDragged(true);
    }

    if (!isDragging) return; // Don't scroll until we're actually dragging

    e.preventDefault();

    const currentTime = Date.now();
    const timeDiff = currentTime - lastMoveTime;

    // Calculate velocity for momentum
    if (timeDiff > 0) {
      const velocityCalc = ((currentX - lastMoveX) / timeDiff) * 16;
      setVelocity(velocityCalc);
    }

    setLastMoveTime(currentTime);
    setLastMoveX(currentX);

    const walk = (x - startX) * 1.5;

    if (carouselRef.current) {
      const newScrollLeft = scrollLeft - walk;
      const maxScroll =
        carouselRef.current.scrollWidth - carouselRef.current.clientWidth;

      // Infinite scroll behavior during drag
      if (newScrollLeft < 0) {
        carouselRef.current.scrollLeft = maxScroll + newScrollLeft;
        setScrollLeft(maxScroll + newScrollLeft);
        setStartX(x);
      } else if (newScrollLeft > maxScroll) {
        carouselRef.current.scrollLeft = newScrollLeft - maxScroll;
        setScrollLeft(newScrollLeft - maxScroll);
        setStartX(x);
      } else {
        carouselRef.current.scrollLeft = newScrollLeft;
      }
    }
  };

  const handleTouchEnd = () => {
    // Track drag end if user was dragging
    if (isDragging) {
      trackEvent('foam-tta-carousel-drag-end', {
        totalItems: items.length,
        selectedIndex,
        interactionType: 'touch',
        hasDragged,
      });
    }

    setIsMouseDown(false);
    setIsDragging(false);
    // Reset hasDragged after a small delay to allow clicks to process
    setTimeout(() => setHasDragged(false), 10);
  };

  const handleArrowClick = (direction: 'left' | 'right') => {
    const newIndex =
      direction === 'left'
        ? (selectedIndex - 1 + items.length) % items.length
        : (selectedIndex + 1) % items.length;

    // Track arrow navigation
    trackEvent('foam-tta-carousel-arrow-click', {
      direction,
      currentIndex: selectedIndex,
      newIndex,
      totalItems: items.length,
      contentId: items[newIndex]?.content_id || 'unknown',
    });

    onSelect(newIndex);
  };

  return (
    <div
      className="relative flex h-12 min-w-0 flex-1 items-center"
      onMouseEnter={() => setShowArrows(true)}
      onMouseLeave={() => setShowArrows(false)}
    >
      {(showArrows || isMobile) && needsScrolling && (
        <>
          <button
            onClick={() => handleArrowClick('left')}
            className="absolute left-0 z-20 flex size-8 items-center justify-center rounded-[9999px] bg-neutral-0 opacity-100 shadow-lg transition-colors hover:bg-neutral-50"
            style={{ backgroundColor: '#ffffff', opacity: 1 }}
          >
            <ChevronDownIcon
              className="rotate-90 text-neutral-800"
              boxSize={4}
            />
          </button>
          <button
            onClick={() => handleArrowClick('right')}
            className="absolute right-0 z-20 flex size-8 items-center justify-center rounded-[9999px] bg-neutral-0 opacity-100 shadow-lg transition-colors hover:bg-neutral-50"
            style={{ backgroundColor: '#ffffff', opacity: 1 }}
          >
            <ChevronDownIcon
              className="-rotate-90 text-neutral-800"
              boxSize={4}
            />
          </button>
        </>
      )}
      <div
        ref={carouselRef}
        className={`flex h-full min-w-0 select-none items-center gap-2 overflow-x-auto px-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDragStart={(e) => e.preventDefault()} // Prevent default drag behavior
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
        }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="shrink-0"
            onDragStart={(e) => e.preventDefault()} // Prevent dragging individual items
            onMouseDown={(e) => {
              // Prevent item clicks from interfering with drag
              if (isDragging) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onTouchStart={(e) => {
              // Prevent item touches from interfering with drag
              if (isDragging) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onClick={(e) => {
              // Only allow clicks when user hasn't dragged
              if (!hasDragged) {
                handleItemSelect(index);
              } else {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            style={{
              pointerEvents: isDragging ? 'none' : 'auto',
            }}
          >
            <ContentItem
              content={item}
              isSelected={index === selectedIndex}
              onClick={() => {}}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
